import { ArnClientError } from '../ArnClientError';
import { ArnAuthService } from '../auth';
import { ArnHttpService } from './ArnHttpService';
import { ArnHttpRetryOptions } from '@arianee/arn-types';

export class ArnFetchHttpService implements ArnHttpService {
  async fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
    const response = await fetch(url, init);
    if (response.ok) {
      return (await response.json()) as T;
    } else {
      throw new ArnClientError(`Could not fetch from ${url}`);
    }
  }

  async fetch(url: string, init?: RequestInit): Promise<Response> {
    const response = await fetch(url, init);
    if (response.ok) {
      return response;
    } else {
      throw new ArnClientError(`Could not fetch from ${url}`);
    }
  }

  async fetchAuthorizedJson<T>(
    auth: ArnAuthService,
    url: string,
    init?: RequestInit
  ): Promise<T> {
    try {
      const signature = auth.generateAuthorizationHeader();
      init = {
        ...init,
        headers: { ...init?.headers, Authorization: signature },
      };
    } catch (e) {
      // Let's see if the data path is allowed without auth by the server
    }
    return this.fetchJson(url, init);
  }

  async sendJson<T = void>(
    url: string,
    payload: object,
    init?: RequestInit
  ): Promise<T> {
    const headers = { ...init?.headers, 'Content-Type': 'application/json' };
    const body = JSON.stringify(payload);
    const response = await fetch(url, {
      ...init,
      method: 'POST',
      headers,
      body,
    });
    const result = await response.json();
    if (response.ok) {
      return result as T;
    } else {
      throw new ArnClientError(
        `Could not post to ${url}: ${result.error} (${result.statusCode}): ${result.message}`
      );
    }
  }

  async sendAuthorizedJson<T>(
    auth: ArnAuthService,
    url: string,
    payload: object,
    init?: RequestInit
  ): Promise<T> {
    try {
      const signature = auth.generateAuthorizationHeader();
      init = {
        ...init,
        headers: { ...init?.headers, Authorization: signature },
      };
    } catch (e) {
      // Let's see if the data path is allowed without auth by the server
    }
    return this.sendJson(url, payload, init);
  }

  retry<T>(doIt: () => Promise<T>, options: ArnHttpRetryOptions): Promise<T> {
    return new Promise((resolve, reject) => {
      let stopTrying: any;
      const failureOption = options.failure;
      let tryCount = 0;
      // eslint-disable-next-line prefer-const
      stopTrying = setInterval(async () => {
        try {
          const result = await doIt();
          clearInterval(stopTrying);
          resolve(result);
        } catch (e) {
          tryCount++;
          options.failure.onFailure?.(tryCount);
        }
        if (tryCount > failureOption.retryCount) {
          clearInterval(stopTrying);
          reject(
            new Error(
              `Failed to execute HTTP request after trying ${failureOption.retryCount} times every ${failureOption.waitDuration} ms`
            )
          );
        }
      }, failureOption.waitDuration);
    });
  }
}
